import { useQuery } from '@tanstack/react-query';
import { concat, isEmpty } from 'lodash';
import { queryClient } from 'src/components/queryProvider';
import { AuthClassification } from 'src/hooks/auth/auth.interface';
import { ILocation, ILocationListing } from 'src/interfaces';
import { generateQuery, queryKeys, requestCCApi } from 'src/utils';

import { fetchLayoutType } from '../layoutTypes';
import { mapLocations, mapViewLocationsByGroups } from '../schedules/helper';
import { fetchViewLocationsByGroupsWithIncludesAndWithoutPagination } from './useViewLocationsByGroups';

// Be careful when using this hook as it can return a large amount of data if filters are not specific enough
export const useGetLocations = (
  filters: string[] = [],
  userRole?: AuthClassification | null,
) =>
  useQuery({
    queryKey: queryKeys.locations.all(filters),
    queryFn: () => fetchLocations(filters, userRole),
    enabled: !isEmpty(filters),
  });

export const fetchLocations = async (
  filters: string[] = [],
  userRole?: AuthClassification | null,
) => {
  const query = concat(filters, ['pageSize=0']);
  let url;
  if (userRole !== AuthClassification.OWNER_OPERATOR) {
    url = `/locations${generateQuery(
      ['locationGroups', 'locationTags', 'locationOwnerTags'],
      query,
    )}`;
  } else {
    url = `/locations${generateQuery(
      ['locationGroups', 'locationOwnerTags'],
      query,
    )}`;
  }

  const viewLocationsByGroupsFilter: string[] = [];

  for (const filter of filters) {
    if (filter.includes('location_group_id')) {
      const values = filter.split('=')[1];
      viewLocationsByGroupsFilter.push(`locationGroups.id.list=${values}`);
    }
  }

  const [response, viewLocations, layoutTypes] = await Promise.all([
    requestCCApi({ url }),
    queryClient.fetchQuery({
      queryKey: queryKeys.viewLocationsByGroups.all(
        viewLocationsByGroupsFilter,
      ),
      queryFn: () =>
        fetchViewLocationsByGroupsWithIncludesAndWithoutPagination(
          userRole,
          viewLocationsByGroupsFilter,
        ),
    }),
    queryClient.fetchQuery({
      queryKey: queryKeys.layoutTypes.all(),
      queryFn: fetchLayoutType,
    }),
  ]);

  return mapLocations(
    response,
    mapViewLocationsByGroups(viewLocations),
    layoutTypes,
  );
};

export const useGetPaginatedLocations = (
  filters: string[] = [],
  hasUnassigned?: boolean,
  options?: {
    enabled?: boolean;
  },
) => {
  const optionalParams =
    hasUnassigned !== undefined
      ? [`has_unassigned_channels=${hasUnassigned}`]
      : [];

  const hasPageSize = filters.some((f) => f.startsWith('pageSize='));

  const queryParams = concat(
    filters,
    ['count=true'],
    !hasPageSize ? ['pageSize=20'] : [],
    optionalParams,
  );

  const apiQueryParams = queryParams.filter((f) => !f.startsWith('_cacheKey='));

  return useQuery({
    queryKey: queryKeys.locationListing.pagination(queryParams),
    queryFn: async () => {
      const response = await requestCCApi({
        url: `/locationListing${generateQuery([], apiQueryParams)}`,
      });

      return {
        locations: response.data?.map((loc: ILocationListing) => {
          return {
            ...loc,
            total_channels: loc.channels.length,
            unassigned_channels: Math.max(
              loc.channels.length -
                (loc.location_groups?.filter(
                  (locationGroup) => !locationGroup.is_default,
                ).length ?? 0),
              0,
            ),
            channels: loc.channels?.map((c) => c.name) ?? [],
          };
        }) as ILocationListing[],
        totalPages: response.totalPages ?? 0,
        totalCount: response.totalCount ?? 0,
      };
    },
    ...options,
  });
};

export const useGetPaginatedLocationsForLocationGroup = (
  filters: string[] = [],
) => {
  const queryParams = concat(filters, ['pageSize=20'], ['count=true']);

  return useQuery({
    queryKey: queryKeys.locations.pagination(filters),
    queryFn: async () => {
      const response = await requestCCApi({
        url: `/locations${generateQuery([], queryParams)}`,
      });

      return {
        locations: response.data as ILocation[],
        totalPages: response.totalPages ?? 0,
        totalCount: response.totalCount ?? 0,
      };
    },
  });
};

export const useGetPaginatedLocationsForTags = (filters: string[] = []) => {
  let queryParams = concat(
    filters,
    ['pageSize=50'],
    ['count=true'],
    ['sort=-created_at,location_code'],
  );

  return useQuery({
    queryKey: queryKeys.locations.pagination(filters),
    queryFn: async () => {
      const response = await requestCCApi({
        url: `/locations${generateQuery([], queryParams)}`,
      });

      return {
        locations: response.data as ILocation[],
        totalPages: response.totalPages ?? 0,
        totalCount: response.totalCount ?? 0,
      };
    },
  });
};

/**
 * Page size used by the Edit Location Tag page (members-first virtual
 * pagination). Kept in lockstep with the size advertised in the URL so the
 * server's `count=true` `totalCount` lines up with our slicing math.
 */
export const LOCATION_TAG_EDIT_PAGE_SIZE = 50;

/**
 * Sort applied to both the tagged and untagged streams on Edit Location Tag.
 * Tagged rows are presented in this order at the front of the merged list;
 * untagged rows continue in the same order after them.
 */
export const LOCATION_TAG_EDIT_SORT = 'sort=-created_at,location_code';

const LOCATION_TAG_EDIT_CONTROLLED_KEYS = new Set([
  'page',
  'pageSize',
  'sort',
  'count',
]);

const stripTagEditControlledParams = (filters: string[]): string[] =>
  filters.filter((f) => {
    const key = f.split('=')[0];

    return !LOCATION_TAG_EDIT_CONTROLLED_KEYS.has(key);
  });

interface UntaggedPageResult {
  data: ILocation[];
  totalCount: number;
}

interface FetchLocationTagEditPageDataResult {
  locations: ILocation[];
  fullTaggedServerLocations: ILocation[];
  totalPages: number;
  totalCount: number;
}

/**
 * Members-first virtual pagination for the Edit Location Tag page.
 *
 * 1. Fetch the **tagged** set (`location_tag_id={id}` + `pageSize=0`) within
 *    the active filter context. These rows always lead the merged sequence.
 * 2. If there are no tagged members, fall back to the original single-request
 *    pagination so an unmembered tag behaves like the add-tag flow.
 * 3. Otherwise, fetch the **untagged** stream with `location_code.notlist=…`
 *    so members are not duplicated, and slice the visible page out of
 *    `[…tagged, …untagged]`. Untagged page 1 is always fetched (it carries
 *    `totalCount`); additional untagged pages are fetched in parallel only
 *    when the visible slice spans a 50-row boundary.
 *
 * Relies on the `/locations` API supporting `location_tag_id={id}` for the
 * tagged-set query and `location_code.notlist=code1|code2|…` to exclude
 * members from the untagged stream.
 */
export const fetchLocationTagEditPageData = async (
  filters: string[],
  locationTagId: number,
  page: number,
): Promise<FetchLocationTagEditPageDataResult> => {
  const baseFilters = stripTagEditControlledParams(filters);

  const taggedQuery = concat(
    baseFilters,
    [`location_tag_id=${locationTagId}`],
    ['pageSize=0'],
    [LOCATION_TAG_EDIT_SORT],
  );

  const taggedResponse = await requestCCApi({
    url: `/locations${generateQuery([], taggedQuery)}`,
  });
  const tagged = (taggedResponse.data ?? []) as ILocation[];

  if (tagged.length === 0) {
    const fallbackQuery = concat(
      baseFilters,
      [`page=${page}`],
      [`pageSize=${LOCATION_TAG_EDIT_PAGE_SIZE}`],
      ['count=true'],
      [LOCATION_TAG_EDIT_SORT],
    );

    const fallbackResponse = await requestCCApi({
      url: `/locations${generateQuery([], fallbackQuery)}`,
    });

    return {
      locations: (fallbackResponse.data ?? []) as ILocation[],
      fullTaggedServerLocations: [],
      totalPages: fallbackResponse.totalPages ?? 0,
      totalCount: fallbackResponse.totalCount ?? 0,
    };
  }

  const notlistValue = tagged.map((loc) => loc.location_code).join('|');
  const untaggedFilters = concat(
    baseFilters,
    [`location_code.notlist=${notlistValue}`],
    [`pageSize=${LOCATION_TAG_EDIT_PAGE_SIZE}`],
    ['count=true'],
    [LOCATION_TAG_EDIT_SORT],
  );

  const fetchUntaggedPage = async (
    pageNum: number,
  ): Promise<UntaggedPageResult> => {
    const query = concat(untaggedFilters, [`page=${pageNum}`]);
    const response = await requestCCApi({
      url: `/locations${generateQuery([], query)}`,
    });

    return {
      data: (response.data ?? []) as ILocation[],
      totalCount: response.totalCount ?? 0,
    };
  };

  // Map the visible UI page (1-indexed, 50 rows) to global indices, then to
  // untagged-API page numbers (also 50 rows / page) for whatever portion of
  // the slice falls past the tagged set.
  const startGlobal = (page - 1) * LOCATION_TAG_EDIT_PAGE_SIZE;
  const endGlobal = page * LOCATION_TAG_EDIT_PAGE_SIZE;
  const untaggedStartIdx = Math.max(0, startGlobal - tagged.length);
  const untaggedEndIdxExclusive = Math.max(0, endGlobal - tagged.length);

  const untaggedPageNumbers = new Set<number>();
  // Always include page 1: it carries the `totalCount` we need for `totalPages`
  // even when the visible slice is fully covered by tagged rows.
  untaggedPageNumbers.add(1);
  if (untaggedEndIdxExclusive > untaggedStartIdx) {
    const startPage =
      Math.floor(untaggedStartIdx / LOCATION_TAG_EDIT_PAGE_SIZE) + 1;
    const endPage = Math.ceil(
      untaggedEndIdxExclusive / LOCATION_TAG_EDIT_PAGE_SIZE,
    );
    for (let p = startPage; p <= endPage; p++) {
      untaggedPageNumbers.add(p);
    }
  }

  const untaggedPagesByNum = new Map<number, ILocation[]>();
  let untaggedTotalCount = 0;

  await Promise.all(
    Array.from(untaggedPageNumbers).map(async (pageNum) => {
      const r = await fetchUntaggedPage(pageNum);
      untaggedPagesByNum.set(pageNum, r.data);
      untaggedTotalCount = r.totalCount;
    }),
  );

  const pageRows: ILocation[] = [];
  for (let g = startGlobal; g < endGlobal; g++) {
    if (g < tagged.length) {
      pageRows.push(tagged[g]);
      continue;
    }
    const uIdx = g - tagged.length;
    const uPage = Math.floor(uIdx / LOCATION_TAG_EDIT_PAGE_SIZE) + 1;
    const uOff = uIdx % LOCATION_TAG_EDIT_PAGE_SIZE;
    const row = untaggedPagesByNum.get(uPage)?.[uOff];
    if (row) pageRows.push(row);
  }

  const mergedTotalCount = tagged.length + untaggedTotalCount;
  const totalPages = Math.max(
    1,
    Math.ceil(mergedTotalCount / LOCATION_TAG_EDIT_PAGE_SIZE),
  );

  return {
    locations: pageRows,
    fullTaggedServerLocations: tagged,
    totalPages,
    totalCount: mergedTotalCount,
  };
};

/**
 * React-Query wrapper around `fetchLocationTagEditPageData`. Cache key is
 * scoped to the tag id, sidebar filters, and page so it participates in the
 * existing `queryKeys.locations` invalidation triggered after
 * `useUpdateLocations` succeeds.
 */
export const useGetPaginatedLocationsForTagEdit = (
  filters: string[] = [],
  locationTagId: number,
  page: number,
) =>
  useQuery({
    queryKey: queryKeys.locations.pagination([
      'tag-edit',
      String(locationTagId),
      ...filters,
      String(page),
    ]),
    queryFn: () => fetchLocationTagEditPageData(filters, locationTagId, page),
    enabled: Number.isFinite(locationTagId) && locationTagId > 0,
  });
